import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AtSymbolIcon, EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import { Alert } from "../../Components/Alert";
import {
  loginUser,
  useAuthState,
  useAuthDispatch,
  requestConfirmSignIn,
} from "../../Context";
import { Button } from "../../Components/Button";
import apple from "../../Assets/images/socials/apple.png";
import google from "../../Assets/images/socials/google.svg";
import { signInWithRedirect } from "aws-amplify/auth";
import FormLabel from "../../Components/FormLabel";
import AuthButton from "../../Components/AuthButton";

function Login(props) {
  const [email, setemail] = useState("");
  const [password, setpassword] = useState("");
  const [passwordVisible, setpasswordVisible] = useState(false);
  const [requireTotp, setrequireTotp] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [authProvider, setauthProvider] = useState(null);
  const [visibleFormLogin, setvisibleFormLogin] = useState(false);

  const dispatch = useAuthDispatch();
  const { cognitoUser, loading, error } = useAuthState();

  const { t } = useTranslation();

  useEffect(() => {
    if (cognitoUser?.nextStep?.signInStep) {
      setrequireTotp(
        cognitoUser.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
      );
    }
  }, [cognitoUser?.nextStep?.signInStep]);

  useEffect(() => {
    const storedAuthProvider = localStorage.getItem("authProvider");
    if (storedAuthProvider) {
      setauthProvider(storedAuthProvider);
    }
  }, []);

  useEffect(() => {
    if (authProvider !== "Email") {
      setvisibleFormLogin(true);
    } else {
      setvisibleFormLogin(false);
    }
  }, [authProvider]);

  const handleEmailChange = (event) => {
    setemail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setpassword(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    loginUser(dispatch, { email, password });
    saveAuth("Email");
  };

  const saveAuth = (auth) => {
    localStorage.setItem("lastUsedAuth", auth);
  };

  const handleConfirmLogin = (event) => {
    event.preventDefault();
    requestConfirmSignIn(dispatch, {
      code: verificationCode,
    });
  };

  const handleFederatedSignIn = (provider) => {
    signInWithRedirect({
      provider,
    });

    saveAuth(provider);
  };

  const renderLoginForm = () => {
    const shouldShowAlert =
      error ||
      (!cognitoUser?.isSignedIn &&
        (cognitoUser?.nextStep?.signInStep === "CONFIRM_SIGN_UP" ||
          cognitoUser?.nextStep?.signInStep === "RESET_PASSWORD"));

    const getAlertMessage = () => {
      if (error?.message) {
        return error.message;
      }

      if (cognitoUser?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        return "accountNotConfirmedYet";
      }

      if (cognitoUser?.nextStep?.signInStep === "RESET_PASSWORD") {
        return "passwordResetRequired";
      }

      return "Error sign in to your account.";
    };

    return (
      <form
        action="#"
        onSubmit={handleSubmit}
        className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm"
      >
        <p className="text-lg font-medium">{t("Sign in to your account")}</p>

        {shouldShowAlert && (
          <Alert
            type="error"
            message={getAlertMessage()}
            errors={error?.errors}
          />
        )}

        {authProvider !== null && authProvider !== "Email" && (
          <div>
            <div className="flex flex-col gap-6 items-center">
              <Button
                type="button"
                className="w-full min-h-[42px]"
                loading={loading}
                disabled={loading}
                title={t("continueWithAuthButton", { authProvider })}
                onClick={() => handleFederatedSignIn(authProvider)}
              />

              <span className="px-2 text-gray-500 text-sm">
                {t("lastSignInMethod", { authProvider })}
              </span>
            </div>
            <div className="flex items-center my-4">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="px-2 text-gray-500 text-sm">{t("or")}</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
          </div>
        )}

        {(!visibleFormLogin || authProvider === null) && (
          <>
            <div>
              <label htmlFor="email" className="text-sm font-medium">
                {t("Email")}
              </label>

              <div className="relative mt-1">
                <input
                  type="email"
                  id="email"
                  data-testid="email"
                  className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
                  placeholder={t("Enter email")}
                  value={email}
                  onChange={handleEmailChange}
                  required
                />

                <span className="absolute inset-y-0 inline-flex items-center right-4">
                  <AtSymbolIcon className="w-5 h-5 text-gray-400" />
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="text-sm font-medium">
                {t("Password")}
              </label>

              <div className="relative mt-1">
                <input
                  type={passwordVisible ? "text" : "password"}
                  id="password"
                  data-testid="password"
                  className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
                  placeholder={t("Enter password")}
                  value={password}
                  onChange={handlePasswordChange}
                  required
                />

                <button
                  type="button"
                  onClick={() => setpasswordVisible(!passwordVisible)}
                  className="absolute inset-y-0 inline-flex items-center right-4"
                >
                  {passwordVisible ? (
                    <EyeIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <EyeOffIcon className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <p className="text-sm text-center text-gray-500">
              <a className="underline" href="/passwordless-login">
                {t("Log in using code")}
              </a>
            </p>

            <Button
              type="submit"
              data-testid="button-login"
              className="w-full min-h-[42px]"
              loading={loading}
              disabled={loading}
              title={t("Log in")}
            />
          </>
        )}

        <div className="flex flex-col space-y-2">
          {authProvider && visibleFormLogin && (
            <button
              onClick={() => {
                setvisibleFormLogin(false);
              }}
              type="button"
            >
              <div className="relative flex items-center justify-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md min-h-[42px] hover:bg-slate-50">
                <AtSymbolIcon className="w-5 h-5 text-gray-400 absolute left-4" />
                <span className="text-slate-700">
                  {t("signInWithEmailButton")}
                </span>
              </div>
            </button>
          )}
          <AuthButton
            provider="Apple"
            icon={apple}
            label={t("Sign in with Apple")}
            onSignIn={handleFederatedSignIn}
          />
          <AuthButton
            provider="Google"
            icon={google}
            label={t("Sign in with Google")}
            onSignIn={handleFederatedSignIn}
          />
        </div>

        <p className="text-sm text-center text-gray-500">
          {t("No account?")}{" "}
          <a className="underline" href="/signup">
            {t("Sign up")}
          </a>
        </p>

        <p className="text-sm text-center text-gray-500">
          <a className="underline" href="/reset">
            {t("Reset password")}
          </a>
        </p>

        <p className="text-sm text-center text-gray-500">
          <a className="underline" href="/confirm">
            {t("Confirm your account")}
          </a>
        </p>
      </form>
    );
  };

  const renderTotpForm = () => {
    const handleVerificationCodeChange = (e) => {
      const input = e.target.value;
      const sanitizedInput = input.replace(/\D/g, "");
      const truncatedInput = sanitizedInput.slice(0, 6);

      setVerificationCode(truncatedInput);
    };

    const handlePaste = () => {
      navigator.clipboard.readText().then((text) => {
        handleVerificationCodeChange({
          target: { name: "verificationCode", value: text },
        });
      });
    };

    return (
      <form
        action="#"
        onSubmit={handleConfirmLogin}
        className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm"
      >
        <p className="text-lg font-medium">{t("Security Verification")}</p>

        {error ? (
          <Alert
            type="error"
            message={error?.message || "Error sign in to your account."}
            errors={error?.errors}
          />
        ) : null}

        <div>
          <FormLabel
            htmlFor="verificationCode"
            label={t("Authenticator code")}
            required
          />

          <div className="relative mt-1">
            <input
              maxLength="6"
              inputMode="numeric"
              spellCheck="false"
              autoComplete="one-time-code"
              type="text"
              name="verificationCode"
              value={verificationCode}
              placeholder={t("Enter the 6-digit code from the authenticator")}
              onChange={handleVerificationCodeChange}
              className={
                "border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm pr-[70px]"
              }
            />
            <button
              type="button"
              onClick={handlePaste}
              className="text-sm text-blue-600 font-medium absolute inset-y-0 inline-flex items-center right-4"
            >
              {t("Paste")}
            </button>
          </div>
        </div>

        <Button
          loading={loading}
          disabled={loading || verificationCode.length < 6}
          type="submit"
          className="w-full min-h-[42px]"
          title={t("Log in")}
        />
      </form>
    );
  };

  return (
    <div className="max-w-lg mx-auto">
      {requireTotp ? renderTotpForm() : renderLoginForm()}
    </div>
  );
}

export default Login;
